import React from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@components/atoms/badge';
import { Button } from '@components/atoms/button';
import { Spinner } from '@components/atoms/spinner';
import { Typography } from '@components/atoms/typography';
import { CloseCircleIcon } from '@components/icons';
import { AttentionIcon } from '@components/icons/attention-icon';
import { BadgeStatusEnum } from '@models/enums';
import type { HintsProps } from '@modules/dashboard/pages/docs-verification/docs-verification-page/tabs/selfie/liveness/step/liveness-process/hints/types';
import Box from '@mui/material/Box';
import cn from 'classnames';

import { VerificationStatusEnum } from '../types';
import { getHintsPhoto } from '../utils/get-hints-photo';

import { HintsContainer } from './styles';

export const Hints: React.FC<HintsProps> = ({
    faceDetectResult,
    verificationStatus,
    tryLoadFacemesh,
    detectCount,
    minDetectCount,
}) => {
    const { t } = useTranslation();

    const isCanShowHints = detectCount >= minDetectCount;

    return (
        <HintsContainer
            className={cn('hints', {
                'hints--error':
                    verificationStatus === VerificationStatusEnum.FACE_NOT_FOUND ||
                    verificationStatus === VerificationStatusEnum.FACE_DETECTOR_LOADING_ERROR ||
                    verificationStatus === VerificationStatusEnum.POOR_LIGHTING,
            })}
        >
            {(verificationStatus === VerificationStatusEnum.FACE_DETECTOR_LOADING ||
                (verificationStatus === VerificationStatusEnum.READY && detectCount === 0)) && (
                <div className='loading-face-detector'>
                    <Spinner size={20} style={{ marginRight: 16 }} />
                    {t('docVerificationWidget.liveness.loadingFaceDetector')}
                </div>
            )}
            {verificationStatus === VerificationStatusEnum.FACE_DETECTOR_LOADING_ERROR && (
                <Box display='flex' flexDirection='column' gap='16px'>
                    <Box display='flex' justifyContent='center' alignItems='center'>
                        <AttentionIcon />
                        {t('docVerificationWidget.liveness.errorLoadingFaceDetector')}
                    </Box>
                    <Button
                        variant='primary'
                        onClick={tryLoadFacemesh}
                        style={{ maxWidth: 100, margin: 'auto' }}
                    >
                        {t('common.tryAgain')}
                    </Button>
                </Box>
            )}
            {isCanShowHints && verificationStatus === VerificationStatusEnum.FACE_NOT_FOUND && (
                <Box display='flex'>
                    <AttentionIcon />
                    {t('docVerificationWidget.liveness.textMask')}
                </Box>
            )}

            {verificationStatus === VerificationStatusEnum.POOR_LIGHTING && (
                <Badge
                    leftAddon={<CloseCircleIcon />}
                    text={t('docVerificationWidget.liveness.lightError')}
                    status={BadgeStatusEnum.ERROR}
                    className='error-badge'
                />
            )}

            {verificationStatus === VerificationStatusEnum.CHECK_FACE_BY_BE_IN_PROGRESS && (
                <Typography tag='p' variant='h3' className='photo-hints'>
                    {t('docVerificationWidget.liveness.loadingRequest')}
                </Typography>
            )}

            {isCanShowHints &&
                faceDetectResult &&
                verificationStatus !== VerificationStatusEnum.FACE_NOT_FOUND &&
                verificationStatus !== VerificationStatusEnum.POOR_LIGHTING &&
                verificationStatus !== VerificationStatusEnum.CHECK_FACE_BY_BE_IN_PROGRESS && (
                    <p
                        className='photo-hints'
                        dangerouslySetInnerHTML={{
                            __html: t(`${getHintsPhoto(faceDetectResult)}`),
                        }}
                    />
                )}
        </HintsContainer>
    );
};
